import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';
import { ProgressBarModule } from 'primeng/progressbar';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { MatIconModule } from '@angular/material/icon';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../services/auth.service';
import type {
  MitConsentDTO,
  MitConsentDetailDTO,
  MitLimits,
  MitMandateDTO,
  MitTransactionDTO,
  MitTransactionStatusValue,
  MitUsageDTO
} from '../../core/models';
import {
  MIT_CONSENT_TYPE_LABELS,
  MIT_LIMIT_ROWS,
  MIT_MANDATE_STATUS_LABELS,
  MIT_TRANSACTION_STATUS_LABELS,
  MIT_TRANSACTION_TYPE_LABELS,
  MitLimitRow,
  MitService,
  mitApiError,
  mitLimitValue,
  mitMandateActions,
  mitMandateStatusReason,
  mitMandateStatusSeverity,
  mitRejectionLabel,
  mitTransactionStatusSeverity
} from '../../services/mit.service';
import { MitMandateStatusAction, MitMandateStatusDialogComponent } from './mit-mandate-status-dialog.component';
import { MitComercialMetadataViewComponent } from './mit-comercial-metadata-view.component';
import { MitMandateLimitsDialogComponent } from './mit-mandate-limits-dialog.component';
import { MitMandateLinkDialogComponent } from './mit-mandate-link-dialog.component';
import { MitChargeDialogComponent } from './mit-charge-dialog.component';
import { MitConsentReadComponent } from './mit-consent-read.component';

interface UsageBar {
  label: string;
  used: number;
  limit: number | null | undefined;
  kind: 'amount' | 'count';
}

@Component({
  selector: 'app-mit-mandate-read',
  templateUrl: './mit-mandate-read.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host ::ng-deep .sale-form-card.p-card .p-card-header {
        padding: 0;
      }
      :host ::ng-deep .sale-form-card.p-card .p-card-body {
        padding-top: 0.25rem;
      }
      :host ::ng-deep .sale-form-card .p-tablist,
      :host ::ng-deep .sale-form-card .p-tabview-tablist,
      :host ::ng-deep .sale-form-card .p-tabs-nav,
      :host ::ng-deep .sale-form-card [data-pc-section='tablist'] {
        padding-top: 0;
        margin-top: 0;
      }
      :host ::ng-deep .sale-form-card .p-tabpanels,
      :host ::ng-deep .sale-form-card .p-tabpanel {
        padding-top: 0.5rem;
      }
      :host ::ng-deep .mit-consent-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .mit-consent-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [
    CommonModule,
    RouterLink,
    TableModule,
    ButtonModule,
    CardModule,
    TabsModule,
    TagModule,
    SkeletonModule,
    ProgressBarModule,
    TooltipModule,
    DrawerModule,
    MatIconModule,
    MitComercialMetadataViewComponent,
    MitMandateStatusDialogComponent,
    MitMandateLimitsDialogComponent,
    MitMandateLinkDialogComponent,
    MitChargeDialogComponent,
    MitConsentReadComponent
  ]
})
export class MitMandateReadComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private auth = inject(AuthService);
  private toastr = inject(ToastrService);
  private mitService = inject(MitService);

  mandate = signal<MitMandateDTO | null>(null);
  usage = signal<MitUsageDTO | null>(null);
  loading = signal(true);
  notFound = signal(false);
  transactions = signal<MitTransactionDTO[]>([]);
  transactionsLoading = signal(false);
  transactionsTotal = signal(0);
  transactionsPage = signal(0);
  readonly transactionsSize = 10;

  statusAction = signal<MitMandateStatusAction | null>(null);
  limitsOpen = signal(false);
  linkOpen = signal(false);
  chargeOpen = signal(false);
  linkLoading = signal(false);
  manageLinkLoading = signal(false);
  activeTab = signal<string>('general');
  consentOpen = signal(false);
  consentLoading = signal(false);
  selectedConsent = signal<MitConsentDetailDTO | null>(null);
  private consentRequestSeq = 0;

  readonly limitRows = MIT_LIMIT_ROWS;
  readonly canWrite = this.auth.hasRole('MIT_MANDATE_WRITE');
  readonly canCharge = this.auth.hasRole('MIT_TRANSACTION_WRITE');
  readonly canReadTransactions = this.auth.hasRole('MIT_TRANSACTION_READ');

  actions = computed(() => mitMandateActions(this.mandate(), this.auth.hasRole('MIT_CONFIG_WRITE')));
  cardExpiresSoon = computed(() => {
    const card = this.mandate()?.card;
    if (!card?.expirationMonth || !card.expirationYear) return false;
    const year = Number(card.expirationYear.length === 2 ? `20${card.expirationYear}` : card.expirationYear);
    const lastDay = new Date(year, Number(card.expirationMonth), 0, 23, 59, 59);
    const days = (lastDay.getTime() - Date.now()) / 86_400_000;
    return days <= 30;
  });
  /** Mandato activo cuya vigencia aún no inicia o ya terminó: los cobros se rechazan. */
  validityNotice = computed<{ title: string; detail: string } | null>(() => {
    const m = this.mandate();
    if (m?.status !== 'ACTIVE') return null;
    const now = Date.now();
    if (m.validFrom && now < new Date(m.validFrom).getTime()) {
      return {
        title: 'La vigencia del mandato aún no inicia',
        detail: 'El mandato está activo, pero no se podrán realizar cobros hasta el inicio de su vigencia.'
      };
    }
    if (m.validUntil && now >= new Date(m.validUntil).getTime()) {
      return {
        title: 'La vigencia del mandato terminó',
        detail: 'El mandato sigue activo, pero ya no se pueden realizar cobros. Pasará a vencido en breve.'
      };
    }
    return null;
  });
  usageBars = computed<UsageBar[]>(() => {
    const usage = this.usage();
    const limits = usage?.effectiveLimits ?? this.mandate()?.limits;
    if (!usage) return [];
    return [
      { label: 'Monto hoy', used: usage.dayAmount ?? 0, limit: limits?.maxAmountPerDay, kind: 'amount' },
      { label: 'Monto este mes', used: usage.monthAmount ?? 0, limit: limits?.maxAmountPerMonth, kind: 'amount' },
      { label: 'Monto acumulado', used: usage.totalAmount ?? 0, limit: limits?.maxTotalAmount, kind: 'amount' },
      { label: 'Cobros hoy', used: usage.dayCount ?? 0, limit: limits?.maxTransactionsPerDay, kind: 'count' },
      { label: 'Cobros este mes', used: usage.monthCount ?? 0, limit: limits?.maxTransactionsPerMonth, kind: 'count' },
      { label: 'Cobros en total', used: usage.totalCount ?? 0, limit: limits?.maxTotalTransactions, kind: 'count' }
    ];
  });
  hasToolbarAction = computed(() => {
    const a = this.actions();
    return this.canWrite && (a.enrollmentLink || a.resendEnrollmentLink || a.resendManageLink || a.suspend
      || a.reactivate || a.revoke);
  });
  statusChipClass = computed(() => {
    switch (mitMandateStatusSeverity(this.mandate()?.status)) {
      case 'success': return { chip: 'border-emerald-200 bg-emerald-50', label: 'text-emerald-600', value: 'text-emerald-800' };
      case 'info': return { chip: 'border-sky-200 bg-sky-50', label: 'text-sky-600', value: 'text-sky-800' };
      case 'warn': return { chip: 'border-amber-200 bg-amber-50', label: 'text-amber-600', value: 'text-amber-800' };
      case 'danger': return { chip: 'border-rose-200 bg-rose-50', label: 'text-rose-600', value: 'text-rose-700' };
      default: return { chip: 'border-slate-200 bg-white', label: 'text-slate-500', value: 'text-slate-900' };
    }
  });

  constructor() {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) {
      this.loading.set(false);
      this.notFound.set(true);
      return;
    }
    this.load(id, this.route.snapshot.queryParamMap.get('accion') === 'cobrar');
  }

  load(id: number, openCharge = false): void {
    this.loading.set(true);
    this.mitService.getById(id).subscribe({
      next: (response) => {
        this.loading.set(false);
        this.mandate.set(response.body ?? null);
        this.notFound.set(!response.body);
        this.loadUsage();
        this.loadTransactions();
        if (openCharge && this.canCharge && this.actions().charge) {
          this.chargeOpen.set(true);
          void this.router.navigate([], { queryParams: { accion: null }, queryParamsHandling: 'merge', replaceUrl: true });
        }
      },
      error: () => {
        this.loading.set(false);
        this.notFound.set(true);
      }
    });
  }

  reload(): void {
    const id = this.mandate()?.id;
    if (id) this.load(id);
  }

  back(): void {
    void this.router.navigate(['/mit-mandates']);
  }

  onTabChange(value: string | number | undefined): void {
    this.activeTab.set(String(value ?? 'general'));
  }

  onMandateChanged(updated: MitMandateDTO): void {
    this.mandate.update((current) => ({ ...current, ...updated, consents: updated.consents ?? current?.consents }));
    this.loadUsage();
  }

  onCharged(): void {
    this.reload();
  }

  onTransactionsLazyLoad(event: TableLazyLoadEvent): void {
    const page = event.rows ? Math.floor((event.first ?? 0) / event.rows) : 0;
    if (page === this.transactionsPage()) return;
    this.transactionsPage.set(page);
    this.loadTransactions();
  }

  openTransaction(tx: MitTransactionDTO): void {
    if (this.canReadTransactions) void this.router.navigate(['/mit-transactions', tx.id]);
  }

  openConsent(consent: MitConsentDTO): void {
    const mandateId = this.mandate()?.id;
    if (!mandateId || consent.id == null) return;
    const seq = ++this.consentRequestSeq;
    this.selectedConsent.set(null);
    this.consentLoading.set(true);
    this.consentOpen.set(true);
    this.mitService.consent(mandateId, consent.id).subscribe({
      next: (response) => {
        if (seq !== this.consentRequestSeq) return;
        this.selectedConsent.set(response.body ?? null);
        this.consentLoading.set(false);
      },
      error: (err) => {
        if (seq !== this.consentRequestSeq) return;
        this.consentLoading.set(false);
        this.consentOpen.set(false);
        this.toastr.error(mitApiError(err, 'No se pudo cargar el consentimiento').message);
      }
    });
  }

  onConsentDrawerVisibleChange(visible: boolean): void {
    if (visible) return;
    this.consentRequestSeq++;
    this.consentOpen.set(false);
    this.consentLoading.set(false);
    this.selectedConsent.set(null);
  }

  /** La liga nunca se muestra al comercio: se reenvía al correo guardado en el mandato. */
  resendEnrollmentLink(): void {
    const id = this.mandate()?.id;
    if (!id || this.linkLoading()) return;
    this.linkLoading.set(true);
    this.mitService.resendEnrollmentLink(id).subscribe({
      next: (response) => {
        this.linkLoading.set(false);
        if (response.body) this.onMandateChanged(response.body);
        this.toastr.success(`Liga reenviada a ${response.body?.customerEmail ?? 'el cliente'}`);
      },
      error: (err) => {
        this.linkLoading.set(false);
        this.toastr.error(mitApiError(err, 'No se pudo reenviar la liga').message);
        this.reload();
      }
    });
  }

  resendManageLink(): void {
    const id = this.mandate()?.id;
    if (!id || this.manageLinkLoading()) return;
    this.manageLinkLoading.set(true);
    this.mitService.resendManageLink(id).subscribe({
      next: (response) => {
        this.manageLinkLoading.set(false);
        this.toastr.success(`Liga de gestión enviada a ${response.body?.customerEmail ?? 'el cliente'}`);
      },
      error: (err) => {
        this.manageLinkLoading.set(false);
        this.toastr.error(mitApiError(err, 'No se pudo reenviar la liga de gestión').message);
        this.reload();
      }
    });
  }

  copyReference(value: string | undefined): void {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => this.toastr.success('Copiado'));
  }

  statusLabel(): string {
    const status = this.mandate()?.status;
    return status ? MIT_MANDATE_STATUS_LABELS[status] : '—';
  }

  statusReason(): string | null {
    return mitMandateStatusReason(this.mandate());
  }

  statusSeverity = mitMandateStatusSeverity;
  txSeverity = mitTransactionStatusSeverity;

  txStatusLabel(status: MitTransactionStatusValue | undefined): string {
    return status ? MIT_TRANSACTION_STATUS_LABELS[status] : '—';
  }

  txTypeLabel(type: MitTransactionDTO['type']): string {
    return type ? MIT_TRANSACTION_TYPE_LABELS[type] : '—';
  }

  txDetail(tx: MitTransactionDTO): string {
    if (tx.status === 'REJECTED') return mitRejectionLabel(tx.rejectionCode);
    if (tx.status === 'DECLINED') return tx.providerMessage ?? '';
    return '';
  }

  consentLabel(type: string | undefined): string {
    return type ? MIT_CONSENT_TYPE_LABELS[type as keyof typeof MIT_CONSENT_TYPE_LABELS] ?? type : '—';
  }

  limitValue(row: MitLimitRow, limits: MitLimits | null | undefined): string {
    return mitLimitValue(row, limits);
  }

  limitChanged(row: MitLimitRow): boolean {
    const m = this.mandate();
    return !!m?.pendingLimits && m.pendingLimits[row.key] !== m.limits?.[row.key];
  }

  barPercent(bar: UsageBar): number {
    if (!bar.limit) return 0;
    return Math.min(100, Math.round((bar.used / bar.limit) * 100));
  }

  private loadUsage(): void {
    const id = this.mandate()?.id;
    if (!id) return;
    this.mitService.usage(id).subscribe({
      next: (response) => this.usage.set(response.body ?? null),
      error: () => this.usage.set(null)
    });
  }

  private loadTransactions(): void {
    const id = this.mandate()?.id;
    if (!id) return;
    this.transactionsLoading.set(true);
    this.mitService.mandateTransactions(id, {}, {
      page: this.transactionsPage(),
      size: this.transactionsSize,
      sort: ['createdAt,desc']
    }).subscribe({
      next: (response) => {
        this.transactions.set(response.body ?? []);
        this.transactionsTotal.set(response.page?.totalItems ?? response.body?.length ?? 0);
        this.transactionsLoading.set(false);
      },
      error: () => {
        this.transactions.set([]);
        this.transactionsLoading.set(false);
      }
    });
  }
}
